import { produce, reconcile } from "solid-js/store";
export * from "./layout-model";
import {
  batch,
  createMemo,
  createSignal,
  onCleanup,
  type Accessor,
} from "solid-js";
import { createSimpleContext } from "@mux/ui/context";
import { useGlobalSync } from "./global-sync";
import { useServer } from "./server";
import type { SessionScroll } from "./layout-scroll";
import { createPathHelpers, isOutsideWorkspacePath } from "./file/path";
import { createLayoutViewport } from "./layout/viewport";
import { isLayoutRecord } from "./layout/preferences";
import { createLayoutState } from "./layout/state";
import { createLayoutRightSidebar } from "./layout/right-sidebar";
import { createLayoutDesktopPanel } from "./layout/desktop-panel";
import { createLayoutSessionState } from "./layout/session-state";
import { createLayoutProjects } from "./layout/projects";
import {
  DEFAULT_SESSION_WIDTH,
  NAV_SIDEBAR_MIN_WIDTH,
  NAV_SIDEBAR_PAGE_MIN_WIDTH,
  ReviewDiffStyle,
  createSessionKeyReader,
  nextSessionTabsForOpen,
  normalizeSessionTab,
  normalizeSessionTabList,
  parseLayoutScopeKey,
} from "./layout-model";

/** Workspace directories by workspace ID, published by each connected machine. */
type WorkspaceDirectories = Accessor<ReadonlyMap<string, string>>;

export const { use: useLayout, provider: LayoutProvider } = createSimpleContext(
  {
    name: "Layout",
    init: () => {
      const viewport = createLayoutViewport();
      const [directorySources, setDirectorySources] = createSignal<readonly WorkspaceDirectories[]>([]);

      // Workspace IDs are unique across machines, so the first machine that
      // knows one names its directory. A folder outside every workspace is
      // keyed by its own path.
      const scopeRoot = (workspaceID: string) => {
        if (isOutsideWorkspacePath(workspaceID)) return workspaceID;
        for (const source of directorySources()) {
          const directory = source().get(workspaceID);
          if (directory) return directory;
        }
      };

      const sessionPath = (key: string) => {
        const scope = parseLayoutScopeKey(key);
        const root = scope && scopeRoot(scope.workspaceID);
        if (!root) return;
        return createPathHelpers(() => root);
      };

      const [store, setStore, _, ready] = createLayoutState(sessionPath);
      const PENDING_MESSAGE_TTL_MS = 2 * 60 * 1000;
      const sessionState = createLayoutSessionState({ store, setStore, ready });
      /** The navigation sidebar's rendered width: its chosen width, kept clear of the page beside it. */
      const navWidth = createMemo(() =>
        Math.min(
          Math.max(store.sidebar.width, NAV_SIDEBAR_MIN_WIDTH),
          Math.max(NAV_SIDEBAR_MIN_WIDTH, viewport.width() - NAV_SIDEBAR_PAGE_MIN_WIDTH),
        ),
      );
      /** Width right of the navigation sidebar, shared by the session column and the side panels. */
      const panelAreaWidth = createMemo(
        () => viewport.width() - (store.sidebar.opened && !viewport.mobile() ? navWidth() : 0),
      );

      return {
        ready,
        /** Publishes one machine's workspace directories until the returned release runs. */
        registerWorkspaceDirectories(source: WorkspaceDirectories) {
          setDirectorySources((sources) => [...sources, source]);
          return () => {
            setDirectorySources((sources) => sources.filter((item) => item !== source));
          };
        },
        projectOrder: {
          list: () => store.projectOrder,
          /** Orders one machine's workspaces; other machines keep their order. */
          set(ids: string[]) {
            const moved = new Set(ids);
            setStore("projectOrder", [...ids, ...store.projectOrder.filter((id) => !moved.has(id))]);
          },
        },
        handoff: {
          tabs: createMemo(() => store.handoff?.tabs),
          setTabs(dir: string, id: string) {
            setStore("handoff", "tabs", { dir, id, at: Date.now() });
          },
          clearTabs() {
            if (!store.handoff?.tabs) return;
            setStore("handoff", "tabs", undefined);
          },
        },
        sidebar: {
          opened: createMemo(() => store.sidebar.opened),
          open() {
            setStore("sidebar", "opened", true);
          },
          close() {
            setStore("sidebar", "opened", false);
          },
          toggle() {
            setStore("sidebar", "opened", (x) => !x);
          },
          width: createMemo(() => store.sidebar.width),
          shownWidth: navWidth,
          resize(width: number) {
            setStore("sidebar", "width", width);
          },
        },
        terminal: {
          height: createMemo(() => store.terminal.height),
          resize(height: number) {
            setStore("terminal", "height", height);
          },
        },
        review: {
          diffStyle: createMemo(() => store.review?.diffStyle ?? "split"),
          setDiffStyle(diffStyle: ReviewDiffStyle) {
            setStore("review", "diffStyle", diffStyle);
          },
        },
        rightSidebar: createLayoutRightSidebar(store, setStore, ready),
        desktop: createLayoutDesktopPanel(store, setStore),
        stage: {
          width: createMemo(() => store.stage?.width ?? 500),
          resize(width: number) {
            setStore("stage", "width", width);
          },
        },
        session: {
          width: createMemo(
            () => store.session?.width ?? DEFAULT_SESSION_WIDTH,
          ),
          resize(width: number) {
            if (!store.session) {
              setStore("session", { width });
              return;
            }
            setStore("session", "width", width);
          },
        },
        pendingMessage: {
          set(sessionKey: string, messageID: string) {
            if (!sessionKey) return;
            const at = Date.now();
            sessionState.touch(sessionKey);
            const current = store.sessionView[sessionKey];
            if (!current) {
              setStore("sessionView", sessionKey, {
                scroll: {},
                pendingMessage: messageID,
                pendingMessageAt: at,
              });
              sessionState.prune(sessionKey);
              return;
            }

            setStore(
              "sessionView",
              sessionKey,
              produce((draft) => {
                draft.pendingMessage = messageID;
                draft.pendingMessageAt = at;
              }),
            );
          },
          consume(sessionKey: string) {
            const current = store.sessionView[sessionKey];
            const message = current?.pendingMessage;
            const at = current?.pendingMessageAt;
            if (!message || !at) return;

            setStore(
              "sessionView",
              sessionKey,
              produce((draft) => {
                delete draft.pendingMessage;
                delete draft.pendingMessageAt;
              }),
            );

            if (Date.now() - at > PENDING_MESSAGE_TTL_MS) return;
            return message;
          },
        },
        view(sessionKey: string | Accessor<string>) {
          const key = createSessionKeyReader(sessionKey, sessionState.ensureKey);
          const s = createMemo(() => {
            const session = key();
            return (session ? store.sessionView[session] : undefined) ?? { scroll: {} };
          });
          const terminalOpened = createMemo(() => s().terminalOpened ?? false);

          function setTerminalOpened(next: boolean) {
            const session = key();
            if (!session) return;
            const current = store.sessionView[session];

            if (!current) {
              setStore("sessionView", session, {
                scroll: {},
                terminalOpened: next,
              });
              return;
            }

            const value = current.terminalOpened ?? false;
            if (value === next) return;
            setStore("sessionView", session, "terminalOpened", next);
          }

          return {
            scroll(tab: string) {
              const session = key();
              if (!session) return;
              return sessionState.scroll.scroll(session, tab);
            },
            setScroll(tab: string, pos: SessionScroll) {
              const session = key();
              if (!session) return;
              sessionState.scroll.setScroll(session, tab, pos);
            },
            followupCollapsed: {
              get: () => s().followupCollapsed ?? false,
              set(collapsed: boolean) {
                const session = key();
                if (!session) return;
                const current = store.sessionView[session];
                if (!current) {
                  setStore("sessionView", session, {
                    scroll: {},
                    followupCollapsed: collapsed,
                  });
                  return;
                }
                setStore("sessionView", session, "followupCollapsed", collapsed);
              },
            },
            terminal: {
              opened: terminalOpened,
              open() {
                setTerminalOpened(true);
              },
              close() {
                setTerminalOpened(false);
              },
              toggle() {
                setTerminalOpened(!terminalOpened());
              },
            },
          };
        },
        tabs(sessionKey: string | Accessor<string>) {
          const key = createSessionKeyReader(sessionKey, sessionState.ensureKey);
          const path = createMemo(() => sessionPath(key()));
          const tabs = createMemo(() => {
            const session = key();
            const current = session ? store.sessionTabs[session] : undefined;
            if (!isLayoutRecord(current)) return { all: [] };

            return {
              all: Array.isArray(current.all)
                ? normalizeSessionTabList(path(), current.all)
                : [],
              active: normalizeSessionTab(path(), current.active),
            };
          });
          const normalize = (tab: string) => normalizeSessionTab(path(), tab);
          const normalizeAll = (all: string[]) =>
            normalizeSessionTabList(path(), all);
          return {
            tabs,
            active: createMemo(() => tabs().active),
            all: createMemo(() => tabs().all),
            setActive(tab: string | undefined) {
              const session = key();
              if (!session) return;
              const next = tab ? normalize(tab) : tab;
              if (!store.sessionTabs[session]) {
                setStore("sessionTabs", session, { all: [], active: next });
              } else {
                setStore("sessionTabs", session, "active", next);
              }
            },
            setAll(all: string[]) {
              const session = key();
              if (!session) return;
              const next = normalizeAll(all);
              if (!store.sessionTabs[session]) {
                setStore("sessionTabs", session, {
                  all: next,
                  active: undefined,
                });
              } else {
                setStore("sessionTabs", session, "all", next);
              }
            },
            async open(tab: string) {
              const session = key();
              const normalized = normalize(tab);
              if (!session || !normalized) return;
              const next = nextSessionTabsForOpen(
                store.sessionTabs[session],
                normalized,
              );
              setStore("sessionTabs", session, reconcile(next));
            },
            close(tab: string) {
              const session = key();
              const current = store.sessionTabs[session];
              if (!current) return;

              const all = current.all.filter((x) => x !== tab);
              if (current.active !== tab) {
                setStore("sessionTabs", session, "all", all);
                return;
              }

              const index = current.all.findIndex((f) => f === tab);
              const next =
                current.all[index - 1] ?? current.all[index + 1] ?? all[0];
              batch(() => {
                setStore("sessionTabs", session, "all", all);
                setStore("sessionTabs", session, "active", next);
              });
            },
            move(tab: string, to: number) {
              const session = key();
              const current = store.sessionTabs[session];
              if (!current) return;
              const index = current.all.findIndex((f) => f === tab);
              if (index === -1) return;
              setStore(
                "sessionTabs",
                session,
                "all",
                produce((opened) => {
                  opened.splice(to, 0, opened.splice(index, 1)[0]);
                }),
              );
            },
          };
        },
        get windowWidth() {
          return viewport.width();
        },
        panelAreaWidth,
        compactViewport: viewport.compact,
        mobileLayout: viewport.mobile,
      };
    },
  },
);

/** One machine's workspaces, ordered by the dashboard-wide workspace order. */
export const { use: useMachineProjects, provider: MachineProjectsProvider } = createSimpleContext({
  name: "MachineProjects",
  gate: false,
  init: () => {
    const layout = useLayout();
    const globalSync = useGlobalSync();
    const server = useServer();
    const projects = createLayoutProjects({ globalSync, server, order: layout.projectOrder.list });
    const directories = createMemo(() => new Map(globalSync.data.project.map((project) => [project.id, project.directory])));
    onCleanup(layout.registerWorkspaceDirectories(directories));
    return {
      list: projects.list,
      open(id: string, _directory: string, _name?: string) {
        if (!server.key) return;
        void globalSync.sidebar.load(id);
      },
      close(_id: string) {
        // Driven directly by database state
      },
      expand(id: string) {
        server.projects.expand(id);
      },
      collapse(id: string) {
        server.projects.collapse(id);
      },
      reorder(ids: string[]) {
        layout.projectOrder.set([...ids]);
      },
      restore(projects: { id: string }[]) {
        layout.projectOrder.set(projects.map((p) => p.id).filter(Boolean));
      },
    };
  },
});
